/**
 * Tarjeta de Tendencias de Revenue
 * 
 * Visualización avanzada de tendencias de ingresos con análisis predictivo
 * basado en datos históricos y patrones de comportamiento.
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Grid,
  useTheme
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart,
  Bar
} from 'recharts';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';

/**
 * Tooltip personalizado para el gráfico
 */
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: 1,
          padding: 2,
          boxShadow: 2
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Typography
            key={index}
            variant="body2"
            sx={{ color: entry.color }}
          >
            {entry.name}: {entry.value?.toLocaleString('es-AR', {
              style: 'currency',
              currency: 'ARS',
              maximumFractionDigits: 0
            })}
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

/**
 * Componente para mostrar estadísticas de tendencia
 */
const TrendStats = ({ data, period }) => {
  const theme = useTheme();
  
  // Calcular estadísticas de tendencia
  const stats = useMemo(() => {
    if (!data || data.length < 2) return null;
    
    const recent = data.slice(-7); // Últimos 7 días
    const previous = data.slice(-14, -7); // 7 días anteriores
    
    const recentAvg = recent.reduce((sum, item) => sum + (item.revenue || 0), 0) / recent.length;
    const previousAvg = previous.reduce((sum, item) => sum + (item.revenue || 0), 0) / previous.length;
    
    const growth = previousAvg > 0 ? ((recentAvg - previousAvg) / previousAvg) * 100 : 0;
    const total = data.reduce((sum, item) => sum + (item.revenue || 0), 0);
    const peak = Math.max(...data.map(item => item.revenue || 0));
    const avg = total / data.length;
    
    return {
      growth: growth.toFixed(1),
      total,
      peak,
      average: avg,
      isPositive: growth > 0
    };
  }, [data]);

  if (!stats) return null;

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={6} md={3}>
        <Box textAlign="center">
          <Typography variant="h6" color="primary">
            {stats.total.toLocaleString('es-AR', {
              style: 'currency',
              currency: 'ARS',
              maximumFractionDigits: 0
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total {period}
          </Typography>
        </Box>
      </Grid>
      
      <Grid item xs={6} md={3}>
        <Box textAlign="center">
          <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
            {stats.isPositive ? (
              <TrendingUpIcon color="success" fontSize="small" />
            ) : (
              <TrendingDownIcon color="error" fontSize="small" />
            )}
            <Typography 
              variant="h6" 
              color={stats.isPositive ? 'success.main' : 'error.main'}
            >
              {stats.growth}%
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Tendencia 7d
          </Typography>
        </Box>
      </Grid>
      
      <Grid item xs={6} md={3}>
        <Box textAlign="center">
          <Typography variant="h6">
            {stats.peak.toLocaleString('es-AR', {
              style: 'currency',
              currency: 'ARS',
              maximumFractionDigits: 0
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Pico máximo
          </Typography>
        </Box>
      </Grid>
      
      <Grid item xs={6} md={3}>
        <Box textAlign="center">
          <Typography variant="h6">
            {stats.average.toLocaleString('es-AR', {
              style: 'currency',
              currency: 'ARS',
              maximumFractionDigits: 0
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Promedio diario
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

/**
 * Componente principal de tendencias de revenue
 */
const RevenueTrendsCard = ({ data = [], period = '30 días' }) => {
  const [viewMode, setViewMode] = useState('line');
  const theme = useTheme();

  // Procesar datos para el gráfico
  const chartData = useMemo(() => {
    return data.map(item => ({
      ...item,
      fecha: new Date(item.date || item.fecha).toLocaleDateString('es-AR', {
        day: 'numeric',
        month: 'short'
      }),
      revenue: item.revenue || item.ventas || 0,
      orders: item.orders || item.ordenes || 0
    }));
  }, [data]);

  // Calcular línea de tendencia
  const trendLine = useMemo(() => {
    if (chartData.length < 2) return null;
    
    const n = chartData.length;
    const sumX = chartData.reduce((sum, _, i) => sum + i, 0);
    const sumY = chartData.reduce((sum, item) => sum + item.revenue, 0);
    const sumXY = chartData.reduce((sum, item, i) => sum + i * item.revenue, 0);
    const sumXX = chartData.reduce((sum, _, i) => sum + i * i, 0);
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    return chartData.map((item, i) => ({
      ...item,
      trend: slope * i + intercept
    }));
  }, [chartData]);

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Generar insights automáticos
  const generateInsights = () => {
    if (!chartData || chartData.length < 7) return [];
    
    const insights = [];
    const recent = chartData.slice(-3);
    const recentAvg = recent.reduce((sum, item) => sum + item.revenue, 0) / recent.length;
    const totalAvg = chartData.reduce((sum, item) => sum + item.revenue, 0) / chartData.length;
    
    if (recentAvg > totalAvg * 1.2) {
      insights.push({
        type: 'success',
        message: 'Los ingresos recientes están 20% por encima del promedio del período'
      });
    } else if (recentAvg < totalAvg * 0.8) {
      insights.push({
        type: 'warning',
        message: 'Los ingresos recientes están por debajo del promedio del período'
      });
    }
    
    return insights;
  };

  const insights = generateInsights();

  return (
    <Card elevation={3} sx={{ height: '100%' }}>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <TimelineIcon />
            <Typography variant="h6">Tendencias de Revenue</Typography>
          </Box>
        }
        subheader={`Análisis de ingresos de los últimos ${period}`}
        action={
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="line">Línea</ToggleButton>
            <ToggleButton value="area">Área</ToggleButton>
            <ToggleButton value="combo">Combo</ToggleButton>
          </ToggleButtonGroup>
        }
      />
      
      <CardContent>
        <TrendStats data={chartData} period={period} />
        
        <Box sx={{ height: 300, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            {viewMode === 'line' && (
              <LineChart data={trendLine || chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis 
                  dataKey="fecha" 
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  axisLine={false}
                />
                <YAxis 
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  axisLine={false}
                  tickFormatter={(value) => 
                    value.toLocaleString('es-AR', { 
                      notation: 'compact',
                      compactDisplay: 'short'
                    })
                  }
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke={theme.palette.primary.main}
                  strokeWidth={3}
                  dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: theme.palette.primary.main, strokeWidth: 2 }}
                />
                {trendLine && (
                  <Line
                    type="monotone"
                    dataKey="trend"
                    stroke={theme.palette.secondary.main}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    activeDot={false}
                  />
                )}
              </LineChart>
            )}
            
            {viewMode === 'area' && (
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis 
                  dataKey="fecha" 
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  axisLine={false}
                />
                <YAxis 
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  axisLine={false}
                  tickFormatter={(value) => 
                    value.toLocaleString('es-AR', { 
                      notation: 'compact',
                      compactDisplay: 'short'
                    })
                  }
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke={theme.palette.primary.main}
                  fill={theme.palette.primary.light}
                  fillOpacity={0.3}
                />
              </ComposedChart>
            )}
            
            {viewMode === 'combo' && (
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis 
                  dataKey="fecha" 
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  axisLine={false}
                />
                <YAxis 
                  yAxisId="revenue"
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  axisLine={false}
                  tickFormatter={(value) => 
                    value.toLocaleString('es-AR', { 
                      notation: 'compact',
                      compactDisplay: 'short'
                    })
                  }
                />
                <YAxis 
                  yAxisId="orders"
                  orientation="right"
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  yAxisId="orders"
                  dataKey="orders"
                  fill={theme.palette.secondary.light}
                  opacity={0.6}
                />
                <Line
                  yAxisId="revenue"
                  type="monotone"
                  dataKey="revenue"
                  stroke={theme.palette.primary.main}
                  strokeWidth={3}
                  dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
                />
              </ComposedChart>
            )}
          </ResponsiveContainer>
        </Box>

        {insights.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {insights.map((insight, index) => (
              <Alert 
                key={index} 
                severity={insight.type} 
                variant="outlined"
                sx={{ fontSize: '0.875rem', mb: 1 }}
              >
                {insight.message}
              </Alert>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

RevenueTrendsCard.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string,
    fecha: PropTypes.string,
    revenue: PropTypes.number,
    ventas: PropTypes.number,
    orders: PropTypes.number,
    ordenes: PropTypes.number
  })),
  period: PropTypes.string
};

export default RevenueTrendsCard;
